import { useEffect } from 'react';
import {
  isExitEmergencyAsset,
  isRcdAsset,
  isWorkshopAsset,
  matchSingleFailureOption,
  splitFailureReasons,
  WORKSHOP_CHECKLIST,
} from '../constants/AssetTestConfig';
import { PUSH_BUTTON_FAILURE_DEFECT, validateRcdResult } from '../lib/testValidators';

/**
 * Hydrate RecordTestModal's form (and local photo/checklist/exit-emergency/
 * RCD state) from a loaded existing test. Extracted from RecordTestModal.jsx
 * (Brief 44).
 *
 * The exit/emergency and RCD reconstructions both replace guessing with an
 * exact match against the current dropdown options. A stored `defects_found`
 * can hold something the dropdown cannot represent (two or more reasons
 * joined by `', '`) — `matchSingleFailureOption` returns `null` for that
 * case rather than guessing, and the original text is surfaced read-only via
 * `setExitEmergencyOriginalText` / `setRcdOriginalText` so an edit is never
 * made blind. See Brief 36 Part 3 and Brief 37 Part 4 — this is the
 * data-loss guard, not a cosmetic detail.
 *
 * RCD (Brief 37): the visual pass/fail gate is gone, so a stored fail can no
 * longer be attributed to "visual" vs "everything else". Restoring the
 * dropdown therefore strips the reasons the *measurements* themselves would
 * have written — using the same code that writes them
 * (`PUSH_BUTTON_FAILURE_DEFECT`, `validateRcdResult`'s `autoDefect`), not a
 * second copy of those strings — and matches whatever text remains against
 * the current options. The push-button answer is inferred the same way
 * (presence of `PUSH_BUTTON_FAILURE_DEFECT` in the stored text), not from the
 * overall result, because a reason-only fail (e.g. "Poor Condition") must
 * still show Push Button as the Pass it actually was — every write path
 * blocks the save until push button has a real answer, so one always exists.
 *
 * @param {object} args
 * @param {object|undefined} args.existingTest — loaded test (from useQuery).
 * @param {object|null} args.asset
 * @param {Function} args.reset — react-hook-form reset.
 * @param {Function} args.setValue — react-hook-form setValue.
 * @param {Array} args.testOptions — asset-type test options (for the testType fallback).
 * @param {string|undefined} args.initialTestType
 * @param {Function} args.getDefaultTestType — (testOptions, initialTestType) => string.
 * @param {Function} args.setPhotoUrl
 * @param {Function} args.setShowExitEmergencyFailReasons
 * @param {Function} args.setExitEmergencyOriginalText — receives the stored
 *   text when it cannot be matched to exactly one option, or `null`.
 * @param {Function} args.setRcdOriginalText — the RCD equivalent, receives
 *   the stored text when, after stripping the measurement-derived reasons,
 *   more than one (or one unrecognised) reason remains, or `null`.
 * @param {Function} args.setChecklistValues
 */
export function useExistingTestHydration({
  existingTest,
  asset,
  reset,
  setValue,
  testOptions,
  initialTestType,
  getDefaultTestType,
  setPhotoUrl,
  setShowExitEmergencyFailReasons,
  setExitEmergencyOriginalText,
  setRcdOriginalText,
  setChecklistValues,
}) {
  useEffect(() => {
    if (!existingTest || !asset) return;
    const t = existingTest;
    const photoUrlFromApi = t.photo_url ?? t.photoUrl ?? null;
    setPhotoUrl(photoUrlFromApi);

    const defects = t.defects_found ?? t.defectsFound ?? '';
    const tripMs = t.metrics?.trip_time_ms ?? null;

    // Every RCD save path (modal and card) blocks until push button has a
    // real Pass/Fail answer, so a stored row always carries one — infer it
    // from the derived failure string, not the overall result.
    const rcdReasons = typeof defects === 'string' ? splitFailureReasons(defects) : [];
    const isPushButtonFail = rcdReasons.includes(PUSH_BUTTON_FAILURE_DEFECT);
    const initialRcdButton = asset && isRcdAsset(asset.asset_type) ? (isPushButtonFail ? 'fail' : 'pass') : '';

    // A measured trip time over the limit writes the same autoDefect string
    // validateRcdResult would produce for that exact number — re-derive it
    // (not a duplicated string) so it can be stripped before matching the
    // dropdown against what's left.
    const rcdTripTimeAutoDefect =
      asset && isRcdAsset(asset.asset_type) && tripMs != null
        ? validateRcdResult(tripMs, 'pass').autoDefect
        : null;
    const rcdDerivedReasons = [PUSH_BUTTON_FAILURE_DEFECT, rcdTripTimeAutoDefect].filter(Boolean);

    const notesParts = [t.notes, t.repair_notes]
      .filter((s) => s != null && String(s).trim() !== '');
    const combinedComments = notesParts.join('\n\n');

    const values = {
      testType: t.test_type ?? t.testType ?? getDefaultTestType(testOptions, initialTestType),
      result: t.result ?? 'pass',
      commentsRecommendations: combinedComments,
      tripTimeMs: tripMs != null ? String(tripMs) : '',
      failureReason: '',
      rcdPushButton: initialRcdButton,
      rcdTripTimeMs: tripMs != null ? String(tripMs) : '',
      rcdFailureReason: '',
      photoUrl: photoUrlFromApi,
    };

    // Reconstruct the RCD failure-reason dropdown: strip the reasons the
    // measurements themselves derive, then match what's left against the
    // current options (Brief 37 Part 4). Never auto-pick one of several,
    // and never drop a reason that couldn't be matched.
    if (asset && isRcdAsset(asset.asset_type)) {
      const remaining = rcdReasons.filter((r) => !rcdDerivedReasons.includes(r));
      if (remaining.length > 0) {
        const matched = matchSingleFailureOption(remaining.join(', '), asset.asset_type);
        if (matched) {
          values.rcdFailureReason = matched;
          setRcdOriginalText(null);
        } else {
          // Either the remainder doesn't resolve to exactly one known
          // option, or it resolves to none (e.g. the retired legacy
          // 'Visual Defect'). Leave the dropdown unselected and surface the
          // full original text read-only rather than guessing or dropping it.
          setRcdOriginalText(defects);
        }
      } else {
        setRcdOriginalText(null);
      }
    }

    // Reconstruct the exit/emergency failure-reason dropdown from the stored
    // text. Exact match only, against the current options and legacy
    // checkbox labels alike (matchSingleFailureOption knows both).
    if (asset && isExitEmergencyAsset(asset.asset_type)) {
      const isFail = (t.result ?? '') === 'fail';
      const hasText = typeof defects === 'string' && defects.trim() !== '';
      const matched = hasText ? matchSingleFailureOption(defects, asset.asset_type) : null;

      if (matched) {
        values.failureReason = matched;
        setExitEmergencyOriginalText(null);
      } else if (isFail && !hasText) {
        // "Ghost Pass" guard: a fail with nothing recorded at all (no text to
        // lose) selects 'Other', so the document never shows a failure with
        // no stated reason. This is the original bug-fix behaviour, ported
        // from the checkbox version — not a guess, since there is no
        // original text here to overwrite.
        values.failureReason = 'Other';
        setExitEmergencyOriginalText(null);
      } else if (isFail && hasText) {
        // Text exists but doesn't resolve to exactly one option — either it
        // matches none (e.g. the retired 'Failed Visual Inspection
        // (Diffuser/Body)') or it joins two or more reasons with ', ' from
        // before single-select. Never auto-pick one: leave the dropdown
        // unselected and surface the original text read-only. Brief 36 Part 3.
        values.failureReason = '';
        setExitEmergencyOriginalText(defects);
      } else {
        setExitEmergencyOriginalText(null);
      }

      setShowExitEmergencyFailReasons(isFail || !!values.failureReason);
    }

    reset(values);
    setValue('photoUrl', photoUrlFromApi ?? null);

    // Workshop: populate checklist from metrics
    if (asset && isWorkshopAsset(asset.asset_type) && t.metrics && typeof t.metrics === 'object') {
      const loaded = {};
      WORKSHOP_CHECKLIST.forEach(({ key }) => {
        const v = t.metrics[key];
        if (v === 'pass' || v === 'fail') loaded[key] = v;
      });
      setChecklistValues(loaded);
    }
  }, [
    existingTest,
    asset,
    reset,
    setValue,
    testOptions,
    initialTestType,
    getDefaultTestType,
    setPhotoUrl,
    setShowExitEmergencyFailReasons,
    setExitEmergencyOriginalText,
    setRcdOriginalText,
    setChecklistValues,
  ]);
}
